import { useCallback, useEffect, useRef } from 'react'
import { useIntercom } from 'react-use-intercom'

import { useLazyGetChurnkeyUserHashQuery } from '@/app/services/integrations'
import { contactSupport } from '@/common/helpers/contactSupport'

import { useAuth } from './useAuth'
import { useChurnkeyLoader } from './useChurnkeyLoader'
import { useToolBoxTreatments } from './useToolBoxTreatments'

const PROVIDER = 'stripe'
const FAILED_PAYMENT_GRACE_PERIOD_DAYS = 14

/**
 * useChurnkeyWalls — Returns `runChurnkeyWalls`, a function that gates the
 * authenticated app behind Churnkey's failed-payment and pause walls.
 *
 * Called once from `useAppInitialisation` after organisation context is
 * loaded. Awaits the Churnkey SDK script, fetches a fresh HMAC, then calls
 * `window.churnkey.check('failed-payment', ...)` and
 * `window.churnkey.check('pause', ...)` with logout / support / error
 * callbacks wired into the rest of the app.
 *
 * Per-wall enforcement is controlled by Split.io raw-value treatments
 * (`failedPaymentWallMode`, `pauseWallMode`): `'off'` skips the wall, `'soft'`
 * forces a dismissible wall, anything else (default `'control'`) enforces
 * the standard ticket-specified behavior.
 *
 * Fails open: any error (script load, HMAC fetch, missing SDK) is logged
 * and the wall is skipped — the app stays usable so a transient network
 * failure cannot lock the user out.
 *
 * @returns {{ runChurnkeyWalls: ({ customerId: string, firstSubscriptionId: string }) => Promise<void> }}
 */
export const useChurnkeyWalls = () => {
    const { loadChurnkey } = useChurnkeyLoader()
    const [getChurnkeyUserHash] = useLazyGetChurnkeyUserHashQuery()
    const { failedPaymentWallMode, pauseWallMode, showIntercom } = useToolBoxTreatments()
    const { logout } = useAuth()
    const { show: intercomShow } = useIntercom()

    const intercomRef = useRef({ enabled: showIntercom, show: intercomShow })

    useEffect(() => {
        intercomRef.current = { enabled: showIntercom, show: intercomShow }
    }, [showIntercom, intercomShow])

    const runChurnkeyWalls = useCallback(
        async ({ customerId, firstSubscriptionId }) => {
            if (!customerId) return

            try {
                await loadChurnkey()
            } catch (error) {
                console.error('[Churnkey] script load failed; skipping wall checks', error)
                return
            }

            if (!window.churnkey?.check) {
                console.error('[Churnkey] window.churnkey.check unavailable after script load')
                return
            }

            let authHash
            try {
                authHash = (await getChurnkeyUserHash().unwrap()).trim()
            } catch (error) {
                console.error('[Churnkey] failed to fetch HMAC; skipping wall checks', error)
                return
            }

            const appId = import.meta.env.VITE_CHURNKEY_APP_ID
            const mode = import.meta.env.VITE_CHURNKEY_MODE

            const handleSupportRequest = () =>
                contactSupport({
                    intercomEnabled: intercomRef.current.enabled,
                    intercomShow: intercomRef.current.show,
                })
            const handleLogout = () => logout()
            const onError = (error, type) => console.error('[Churnkey] wall callback error', { error, type })

            if (failedPaymentWallMode !== 'off') {
                window.churnkey.check('failed-payment', {
                    appId,
                    authHash,
                    customerId,
                    mode,
                    provider: PROVIDER,
                    softWall: failedPaymentWallMode === 'soft',
                    gracePeriodDays: FAILED_PAYMENT_GRACE_PERIOD_DAYS,
                    subscriptionId: firstSubscriptionId,
                    handleSupportRequest,
                    handleLogout,
                    onError,
                })
            }

            if (pauseWallMode !== 'off') {
                window.churnkey.check('pause', {
                    appId,
                    authHash,
                    customerId,
                    mode,
                    provider: PROVIDER,
                    subscriptionId: firstSubscriptionId,
                    handleLogout,
                    onError,
                })
            }
        },
        [failedPaymentWallMode, pauseWallMode, logout, loadChurnkey, getChurnkeyUserHash]
    )

    return { runChurnkeyWalls }
}
